import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Utils from "../utils/utils";
import { FormCheck } from "react-bootstrap";
import { Link } from "react-router-dom";
import ArtaService from "../services/arta";
import { usePersistStore, useStore } from "../store";

type PaymentMethod = "card" | "bank_transfer";

// Trustap caps card transactions for buyer-protection underwriting; above
// this threshold the only safe path is bank transfer. Currency-agnostic
// here — Trustap quotes are denominated in the listing currency and we
// don't currently mix currencies, so a flat number matches the figures
// the user sees in the modal.
const CARD_PAYMENT_LIMIT = 5000;

interface ConfirmModalProps {
  show: boolean;
  onNo: () => void;
  // (ipAddress, paymentMethod) — both forwarded to PaymentService.create.
  // Return the underlying promise so the popup can keep its submit button
  // locked until the payment has actually been created (or has failed).
  onYes: (
    ipAddress: string,
    paymentMethod: PaymentMethod
  ) => void | Promise<void>;
  title?: string;
  body?: string;
  closeButtonText?: string;
  confirmButtonText?: string;
  showIcon?: boolean;
  data?: any;
}

const ConfirmPaymentPopup: React.FC<ConfirmModalProps> = ({
  show,
  onNo,
  onYes,
  title = "Confirm Payment",
  closeButtonText = "Cancel",
  confirmButtonText = "Pay Now",
  data,
}) => {
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading } = useStore((s: any) => s);
  const [ipAddress, setIpAddress] = useState("");
  const [isAgreed, setIsAgreed] = useState(false);
  const [quote, setQuote] = useState<any>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceChangedWarning, setPriceChangedWarning] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("card");

  useEffect(() => {
    if (show) {
      setIsSubmitting(false);
      setPriceChangedWarning("");
      setPaymentMethod("card");
      // Reset the per-ad quote state every time the popup opens. Without this
      // a stale shipping-quote error (or an old quote) from a previously
      // opened ad would carry over and show on the next open.
      setValidationError("");
      setQuote(null);
    }
  }, [show]);

  const getQuote = useCallback(async () => {
    setValidationError("");
    const result = await ArtaService.getQuote(token, {
      adId: data?.adId?._id,
    });
    if (result?.error) {
      setValidationError(result?.error);
      setIsLoading(false);
      return;
    }
    if (result?.data) {
      setQuote(result?.data);
    }
  }, [data, token, setValidationError, setIsLoading]);

  useEffect(() => {
    if (!show) return;
    if (!data || !data?._id || !token) return;
    getQuote();
  }, [show, data, token, getQuote]);

  useEffect(() => {
    Utils.getIp()
      .then((ip: string) => {
        setIpAddress(ip);
      })
      .catch(() => {
        setValidationError(
          "We couldn't verify your network. Please check your connection and reopen this popup."
        );
      });
  }, []);

  const bidAmount = Number(data?.price) || 0;
  const shippingAmount = Number(quote?.total) || 0;
  const totalAmount = bidAmount + shippingAmount;
  const isCardDisabled = totalAmount > CARD_PAYMENT_LIMIT;

  // Force bank transfer once the total crosses the card limit. Covers the
  // case where the buyer opens the modal at a card-eligible bid and the
  // shipping quote arrives a moment later pushing the total above the
  // limit — we don't want them stuck on a disabled card option.
  useEffect(() => {
    if (isCardDisabled && paymentMethod === "card") {
      setPaymentMethod("bank_transfer");
    }
  }, [isCardDisabled, paymentMethod]);

  return (
    <Modal
      show={show}
      onHide={onNo}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="confirmModal"
    >
      <Modal.Body>
        <div className="confirmModal__icon">
          <i className="bi bi-credit-card-2-front" aria-hidden="true" />
        </div>
        <h2 className="confirmModal__title">{title}</h2>
        <p className="confirmModal__subtitle">
          Secure checkout via Trustap. Review the breakdown below before
          paying.
        </p>

        <div className="confirmModal__summary">
          <div className="confirmModal__summaryRow">
            <span>Winning bid</span>
            <span>{Utils.formatToCurrency(bidAmount)}</span>
          </div>
          <div className="confirmModal__summaryRow confirmModal__summaryRow--muted">
            <span>Shipping (estimated)</span>
            <span>
              {quote ? Utils.formatToCurrency(shippingAmount) : "Calculating…"}
            </span>
          </div>
          <hr className="confirmModal__summaryDivider" />
          <div className="confirmModal__summaryRow confirmModal__summaryRow--total">
            <span>Total</span>
            <span>
              {quote ? Utils.formatToCurrency(totalAmount) : "—"}
            </span>
          </div>
        </div>

        <div className="confirmPaymentMethod">
          <div className="confirmPaymentMethod__label">Payment method</div>
          <div className="confirmPaymentMethod__options">
            <label
              className={`confirmPaymentMethod__option ${
                paymentMethod === "card"
                  ? "confirmPaymentMethod__option--active"
                  : ""
              } ${
                isCardDisabled
                  ? "confirmPaymentMethod__option--disabled"
                  : ""
              }`}
              aria-disabled={isCardDisabled}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                checked={paymentMethod === "card"}
                disabled={isCardDisabled}
                onChange={() => {
                  if (isCardDisabled) return;
                  setPaymentMethod("card");
                }}
              />
              <i className="bi bi-credit-card-2-front" aria-hidden="true" />
              <span>Card</span>
            </label>
            <label
              className={`confirmPaymentMethod__option ${
                paymentMethod === "bank_transfer"
                  ? "confirmPaymentMethod__option--active"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="bank_transfer"
                checked={paymentMethod === "bank_transfer"}
                onChange={() => setPaymentMethod("bank_transfer")}
              />
              <i className="bi bi-bank" aria-hidden="true" />
              <span>Bank transfer</span>
            </label>
          </div>
        </div>

        {paymentMethod === "bank_transfer" && (
          <div className="confirmModal__alert confirmModal__alert--warning">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span>
              Bank transfers can take 1–3 business days. We'll email you
              once funds clear.
            </span>
          </div>
        )}

        {paymentMethod === "card" && !isCardDisabled && (
          <div className="confirmModal__alert confirmModal__alert--warning">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span>
              For card payments, an additional buyer protection fee will be
              calculated on the Trustap payment page.
            </span>
          </div>
        )}

        {isCardDisabled && (
          <div className="confirmModal__alert confirmModal__alert--warning">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span>
              Card payments are not available for totals over{" "}
              {Utils.formatToCurrency(CARD_PAYMENT_LIMIT)}. Please complete
              this purchase by bank transfer.
            </span>
          </div>
        )}

        <div className="confirmModal__terms">
          <FormCheck
            type="checkbox"
            id="confirm-payment-agree"
            label="I agree to the"
            checked={isAgreed}
            onChange={(e) => setIsAgreed(e.target.checked)}
          />
          <Link to="/terms-and-conditions" target="_blank">
            Terms and Conditions
          </Link>
        </div>

        {validationError && (
          <div className="confirmModal__alert confirmModal__alert--danger">
            <i className="bi bi-exclamation-circle" aria-hidden="true" />
            <span>{validationError}</span>
          </div>
        )}
        {priceChangedWarning && (
          <div className="confirmModal__alert confirmModal__alert--warning">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <span>{priceChangedWarning}</span>
          </div>
        )}

        <div className="confirmModal__footer">
          <Button
            className="closeBtn"
            variant="secondary"
            onClick={onNo}
          >
            {closeButtonText}
          </Button>
          <button
            type="button"
            className="confirmModal__primary"
            onClick={async () => {
              if (isSubmitting) return;
              // Lock immediately — BEFORE the awaits below. The quote refetch
              // + Trustap create take ~1s; previously this flag was only set
              // after the refetch, leaving a window where a second click
              // slipped through and created a duplicate transaction.
              setIsSubmitting(true);

              // Shipping rates drift. Refetch the quote right before we
              // submit and check whether the total has changed. If it has,
              // update the displayed price and make the user re-click
              // instead of silently charging them a different amount.
              if (!priceChangedWarning) {
                const fresh = await ArtaService.getQuote(token, {
                  adId: data?.adId?._id,
                });
                if (fresh?.error) {
                  setValidationError(fresh.error);
                  setIsSubmitting(false);
                  return;
                }
                const oldTotal = Number(quote?.total) || 0;
                const newTotal = Number(fresh?.data?.total) || 0;
                if (Math.abs(oldTotal - newTotal) > 0.01) {
                  setQuote(fresh.data);
                  setPriceChangedWarning(
                    `Shipping price updated to ${Utils.formatToCurrency(
                      newTotal
                    )}. Please click "${confirmButtonText}" again to confirm.`
                  );
                  setIsSubmitting(false);
                  return;
                }
              }

              // Stay locked through the parent's async work (create the
              // Trustap transaction + open the payment tab). On success the
              // parent closes the popup; the finally re-enables the button so
              // a failed attempt can be retried.
              try {
                await onYes(ipAddress, paymentMethod);
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={!isAgreed || !!validationError || isSubmitting || !quote}
          >
            {isSubmitting ? "Processing…" : confirmButtonText}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ConfirmPaymentPopup;
